// =============================================================================================
// Bezeichnungen und Rhythmus-Texte fuer den Abschnitt „Automatische Abläufe".
//
// Eigenes Modul, damit die Abdeckung testbar ist, OHNE die komplette Einstellungen-Ansicht zu
// laden (die zieht das halbe Design-Paket mit). Der Test haelt fest, dass jeder Ablauf, den die
// Migrationen anlegen, hier eine deutsche Bezeichnung hat — sonst erscheint im Kunden-UI der
// technische Name (Vorfall 2026-08-24: „Orders full sweep nightly", „Webhook register daily").
// =============================================================================================

/** Kundenfreundliche Bezeichnung + kurze Erklärung je Gambio-Cron-Jobname.
 *  Unbekannte (neue) Jobs erhalten einen humanisierten Fallback-Namen. */
export const CRON_LABELS: Record<string, { label: string; hint: string }> = {
  'gambio-orders-refresh-15m': { label: 'Bestellungen abrufen', hint: 'Neue und geänderte Gambio-Bestellungen werden nach Vario übernommen. Abgefragt wird nur, was sich seit dem letzten Lauf geändert hat.' },
  'gambio-orders-full-sweep-nightly': { label: 'Bestellungen vollständig abgleichen', hint: 'Einmal je Nacht werden ALLE Bestellungen des Shops durchgesehen — das Netz für den Fall, dass eine Bestellung in der Änderungsliste des Shops nicht auftaucht.' },
  'gambio-stocks-sync-5m': { label: 'Lagerbestand abgleichen', hint: 'Bestände werden von Vario nach Gambio übertragen (Überverkaufsschutz).' },
  'gambio-listing-autosync-15m': { label: 'Geänderte Artikeldaten übertragen', hint: 'Name, Beschreibung, Kategorien und Bilder gehen von Vario nach Gambio.' },
  'gambio-export-daily': { label: 'Preise & Bestand exportieren', hint: 'Täglicher Voll-Export von Preisen und Beständen nach Gambio.' },
  'gambio-import-from-shop-15m': { label: 'Shop-Daten nach Vario übernehmen', hint: 'Preise, Aktionspreise und Artikeldaten aus dem Shop gehen nach Vario. Abgefragt wird nur, was sich seit dem letzten Lauf im Shop geändert hat. Läuft nur, wenn als Richtung „Gambio → Vario" eingestellt ist.' },
  'gambio-items-refresh-daily': { label: 'Gambio-Artikel aktualisieren', hint: 'Der Gambio-Artikelstand wird für die Übersicht neu eingelesen.' },
  'gambio-variant-creation-nachlauf': { label: 'Varianten anlegen (Nachlauf)', hint: 'Noch offene Variantenanlagen in Gambio werden nachgezogen.' },
  'gambio-customer-export-15m': { label: 'Kunden exportieren', hint: 'Für den Export vorgemerkte Kunden werden nach Gambio übertragen.' },
  'gambio-categories-hourly': { label: 'Kategorien abgleichen', hint: 'Neue/geänderte Gambio-Kategorien werden in Vario nachgezogen; ohne Änderungen passiert nichts.' },
  // Alt-Name (bis Migration 0239) - bleibt für Shops, deren Cron noch nicht umgezogen ist.
  'gambio-categories-weekly': { label: 'Kategorien aufräumen', hint: 'Der Gambio-Kategoriebaum wird in Vario aktualisiert (Umbenennungen/Verschiebungen).' },
  'gambio-reconcile-removed-listings-1h': { label: 'Entfernte Artikel nachziehen', hint: 'Artikel, die in Vario aus dem Verkaufskanal genommen wurden, werden im Shop nachgezogen (je nach Einstellung auf inaktiv gesetzt).' },
  // Die drei Anmeldungen halten die Benachrichtigungen des Shops aktuell: Vario erfährt dadurch
  // von Änderungen, ohne dass wir dauernd nachfragen müssen. Sie laufen nachts und sind im
  // Regelfall ein Leerlauf — nötig sind sie nur, wenn eine Anmeldung verloren gegangen ist.
  'gambio-webhook-register-daily': { label: 'Shop-Benachrichtigungen anmelden', hint: 'Stellt sicher, dass der Shop Änderungen weiterhin an Vario meldet.' },
  'gambio-article-update-webhook-register-daily': { label: 'Artikel-Benachrichtigungen anmelden', hint: 'Stellt sicher, dass Artikeländerungen aus Vario weiterhin gemeldet werden.' },
  'gambio-customer-order-webhook-register-daily': { label: 'Kunden- und Bestell-Benachrichtigungen anmelden', hint: 'Stellt sicher, dass neue Kunden und Bestellungen weiterhin gemeldet werden.' },
};

/**
 * Rückfall für einen Ablauf, für den es hier noch keine Bezeichnung gibt.
 *
 * Vorher wurde der technische Name zu einem Satz umgebaut („Orders full sweep nightly",
 * „Webhook register daily"). Das ist englischer Maschinentext in einer deutschen Oberfläche —
 * dem Betreiber am 2026-08-24 aufgefallen. Eine echte Übersetzung kann diese Funktion nicht
 * leisten, sie kennt den Ablauf nicht. Also wird sie ehrlich: neutrale deutsche Bezeichnung, der
 * technische Name in Klammern dahinter. Wer ihn sieht, weiß, dass hier eine Bezeichnung fehlt —
 * und sie gehört dann nach `CRON_LABELS`, nicht in eine Auto-Übersetzung.
 */
export function humanizeCronJobname(jobname: string): string {
  return `Automatischer Ablauf (${jobname})`;
}

const WEEKDAYS_DE = ['sonntags', 'montags', 'dienstags', 'mittwochs', 'donnerstags', 'freitags', 'samstags'];

/** Übersetzt die genutzten 5-Feld-Cron-Ausdrücke in Klartext; Fallback = Roh-Ausdruck. */
export function describeCronSchedule(schedule: string): string {
  const parts = schedule.trim().split(/\s+/);
  if (parts.length !== 5) return schedule;
  const min = parts[0] ?? '';
  const hour = parts[1] ?? '';
  const dom = parts[2] ?? '';
  const dow = parts[4] ?? '';
  const everyMin = /^\*\/(\d+)$/.exec(min);
  if (everyMin && hour === '*' && dom === '*' && dow === '*') {
    const n = Number(everyMin[1]);
    return n === 60 ? 'stündlich' : `alle ${n} Minuten`;
  }
  const isNum = (v: string) => /^\d+$/.test(v);
  if (isNum(min) && isNum(hour)) {
    const time = `${hour.padStart(2, '0')}:${min.padStart(2, '0')} Uhr`;
    if (dom === '*' && dow === '*') return `täglich um ${time}`;
    if (dom === '*' && isNum(dow)) {
      const day = WEEKDAYS_DE[Number(dow) % 7] ?? `Wochentag ${dow}`;
      return `wöchentlich, ${day} um ${time}`;
    }
  }
  // Feste Minute bei jeder Stunde ('20 * * * *') → stündlich.
  if (isNum(min) && hour === '*' && dom === '*' && dow === '*') return 'stündlich';
  return schedule;
}
